/**
 * Fields shown for an OIDC provider using `acquisition_mode: direct`.
 */

import type { ReactElement } from 'react';
import type { ProviderFormData, SetProviderField } from './providerFormTypes';

interface DirectProviderFieldsProps {
  form: ProviderFormData;
  setField: SetProviderField;
  hasClientSecret: boolean;
  extraAuthParamsJson: string;
  onExtraAuthParamsChange: (value: string) => void;
}

export function DirectProviderFields({
  form,
  setField,
  hasClientSecret,
  extraAuthParamsJson,
  onExtraAuthParamsChange,
}: DirectProviderFieldsProps): ReactElement {
  return (
    <>
      <label className="dialog__field">
        Issuer URL
        <input
          value={form.issuer_url ?? ''}
          onChange={(e) => { setField('issuer_url', e.target.value || null); }}
        />
      </label>
      <label className="dialog__field">
        Authorization Endpoint
        <input
          value={form.authorization_endpoint ?? ''}
          onChange={(e) =>
            { setField('authorization_endpoint', e.target.value || null); }
          }
        />
      </label>
      <label className="dialog__field">
        Token Endpoint
        <input
          value={form.token_endpoint ?? ''}
          onChange={(e) => { setField('token_endpoint', e.target.value || null); }}
        />
      </label>
      <label className="dialog__field">
        Client ID
        <input
          value={form.client_id ?? ''}
          onChange={(e) => { setField('client_id', e.target.value || null); }}
        />
      </label>
      <label className="dialog__field">
        Client Secret
        <input
          type="password"
          autoComplete="off"
          value={form.client_secret}
          onChange={(e) => { setField('client_secret', e.target.value); }}
        />
        {hasClientSecret && (
          <span className="dialog__help">secret is set — leave blank to keep</span>
        )}
      </label>
      <label className="dialog__field">
        GitHub App Kind
        <select
          value={form.github_app_kind ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            const kind = value === 'app' || value === 'oauth' ? value : null;
            setField('github_app_kind', kind);
            // Defence-in-depth: `buildProviderRequestBody` is the
            // authoritative gate that drops `scopes` for a GitHub App
            // regardless of form state, but clearing it here too keeps
            // the form itself honest -- an admin re-opening this same
            // session shouldn't find a value sitting behind a field
            // that's no longer shown.
            if (kind === 'app') setField('scopes', null);
          }}
        >
          <option value="">Not GitHub / unset</option>
          <option value="app">GitHub App (recommended)</option>
          <option value="oauth">OAuth App (classic)</option>
        </select>
      </label>
      {form.github_app_kind === 'app' ? (
        <p className="dialog__help">
          GitHub Apps take their permissions from the app registration and
          ignore requested scopes.
        </p>
      ) : (
        <label className="dialog__field">
          Scopes
          <input
            value={form.scopes ?? ''}
            onChange={(e) => { setField('scopes', e.target.value || null); }}
            placeholder="openid profile email"
          />
        </label>
      )}
      <label className="dialog__field">
        Extra Auth Params (JSON)
        <textarea
          value={extraAuthParamsJson}
          onChange={(e) => { onExtraAuthParamsChange(e.target.value); }}
          rows={3}
          className="dialog__json"
        />
      </label>
    </>
  );
}
